/**
 * NU DATA shell nav config — ported from
 * NU-DATA-DASHBOARD/kits/nu-data/components/chrome.jsx (NAV_GROUPS /
 * NAV_FOOTER) + kits/_core/admin/index.jsx (NU_ADMIN_NAV_GROUP,
 * NU_PROFILE_NAV_ITEM, NU_SETTINGS_NAV_ITEM), with every kit hash route
 * translated to this SPA's BrowserRouter path via the SAME translation the
 * ratified matrix encodes (src/rbac/matrix.ts translateDocRoute):
 *   `#/` → /dashboard, `#/groups*` → /data-groups*, else strip the `#`.
 *
 * Visibility (NOT enforcement — the live RouteGuard swap is #652, blocked on
 * #641). TWO predicates must BOTH pass before an entry is advertised:
 *
 *  1. the ratified role matrix (src/rbac canAccess, fail-closed: items whose
 *     route has no matrix row are hidden), for sessions whose role
 *     normalizes; non-normalizing sessions (LEGACY_ROLE_UNRATIFIED, e.g. the
 *     live 'customer'/'editor' roles — mapping unratified, #641; plus
 *     UNKNOWN_ROLE garbage) fall back to the legacy SidebarMenu nav. NO_ROLE
 *     sessions never reach the shell (AuthGuard redirects to /login first)
 *     but get the same fallback defensively;
 *  2. the LIVE route guard (src/components/shell/liveRouteGuards
 *     `liveGuardAllows`) — the very table src/routes/index.tsx gates on.
 *
 * (2) is the fix for the nav↔route disagreement: the matrix grants `viewer`
 * List Management (ROLE-MATRIX §3 row 4) while the live guard on /lists
 * admits only admin|editor, so a viewer was shown the entry and then bounced
 * to /dashboard. Filtering by BOTH means nav can never advertise a route the
 * live guard will refuse, in either direction of future drift — there is one
 * guard table and both layers read it. The safe direction is the only one
 * available until #652 lands: HIDE what the router refuses, never widen the
 * router to match the nav.
 *
 * Consequence worth stating plainly: canonical roles with no live-guard
 * counterpart (owner / member / billing / tenant_admin — not issuable by
 * src/hooks/useAuth's UserRole vocabulary today) collapse to the unguarded
 * entries only. That is honest — those sessions genuinely cannot render the
 * guarded pages — and it resolves when #641/#652 ratify the mapping.
 *
 * Kit-vs-repo route notes (kept honest, not silently "fixed"):
 *  - `API Usage`: the matrix path is /admin/api-usage (ROLE-MATRIX §3 row 12
 *    verbatim) but the live route is /admin/api-monitoring — the item
 *    navigates to the real route and checks access against the matrix path.
 *  - `Roles`: same shape. The matrix path is /admin/roles (row 15) and the
 *    live route is /admin/access-control, whose page
 *    (pages/admin/AccessControlManagement) is the kit's own admin-roles.jsx
 *    screen — permissions / Roles tab / user assignments. The item navigates
 *    to the real route and checks access against the matrix path. Before
 *    this, `Roles` pointed at /admin/roles, which the route table has never
 *    mounted: the entry landed on the in-shell 404.
 *  - `Settings` (#/settings): ROLE-MATRIX §3 row 17 assigns it no route, so
 *    canAccess would deny it as UNKNOWN_ROUTE; it resolves visibility by its
 *    matrix row id instead (row 17, all roles).
 *  - `Onboarding` (#/onboarding) and `Enrichment Pipeline`
 *    (#/enrichment-pipeline) are NOT ported here. Neither has a page in this
 *    SPA and neither has a route, so both entries could only ever land on
 *    the in-shell 404. Onboarding additionally has no ratified route in
 *    ROLE-MATRIX row 21, and was already hidden for every canonical role by
 *    canAccess. Inventing pages for them is out of scope; when either screen
 *    is actually built, add the route first and the entry with it.
 *  - `Tenants` has no icon: the kit references icon 'superAdmin' which does
 *    not exist in the kit's own NuIcons set either, so the kit renders no
 *    icon there. Ported verbatim for golden fidelity.
 */

import { canAccess, normalizeRole, SCREEN_MATRIX } from "../../rbac";
import { liveGuardAllows } from "./liveRouteGuards";
import type { NuIconName } from "../shared/icons";

/** Shared navigate-callback shape for all shell components. */
// eslint-disable-next-line no-unused-vars -- param name in a type annotation
export type NavigateFn = (route: string) => void;

export interface ShellNavItem {
  id: string;
  label: string;
  /** NuIcons key; optional (kit's Tenants item resolves no icon). */
  icon?: NuIconName;
  /** Real SPA path this item navigates to. */
  route: string;
  /**
   * Matrix path used for the visibility check when it differs from the live
   * route (API Usage: matrix /admin/api-usage vs live /admin/api-monitoring).
   */
  accessPath?: string;
  /**
   * ROLE-MATRIX §3 row id used for visibility when the doc assigns the screen
   * no route (Settings, row 17). Takes precedence over accessPath/route.
   */
  matrixId?: number;
  children?: readonly ShellNavItem[];
}

export interface ShellNavGroup {
  id: string;
  /** Labeled groups (Administration) render a section header. */
  label?: string;
  items: readonly ShellNavItem[];
}

/* ── chrome.jsx NAV_GROUPS, routes translated ── */

export const NAV_GROUPS: readonly ShellNavGroup[] = [
  {
    id: "main",
    items: [
      {
        id: "dashboard",
        label: "Dashboard",
        icon: "dashboard",
        route: "/dashboard",
      },
      {
        id: "lists",
        label: "List Management",
        icon: "list",
        route: "/lists",
        children: [
          {
            id: "lists-analytics",
            label: "Outcome Analytics",
            icon: "enrich",
            route: "/lists/analytics",
          },
        ],
      },
      {
        id: "enrichment",
        label: "Data Enrichment",
        icon: "enrich",
        route: "/enrichment",
      },
      {
        id: "groups",
        label: "Data Groups",
        icon: "groups",
        route: "/data-groups",
        children: [
          {
            id: "groups-analytics",
            label: "RPC & WPC Analytics",
            icon: "enrich",
            route: "/data-groups/analytics",
          },
          {
            id: "suppression",
            label: "Contact Status",
            icon: "shield",
            route: "/suppression",
          },
        ],
      },
      {
        id: "connections",
        label: "Connections",
        icon: "connections",
        route: "/connections",
        children: [
          {
            id: "connections-mapping",
            label: "Data Mapping",
            icon: "link",
            route: "/connections/mapping",
          },
        ],
      },
      { id: "export", label: "Data Export", icon: "export", route: "/export" },
    ],
  },
  {
    id: "admin",
    label: "Administration",
    items: [
      { id: "admin-tenants", label: "Tenants", route: "/admin/tenants" },
      {
        id: "admin-users",
        label: "Users",
        icon: "user",
        route: "/admin/users",
      },
      {
        id: "admin-roles",
        label: "Roles",
        icon: "list",
        route: "/admin/access-control",
        accessPath: "/admin/roles",
      },
      {
        id: "admin-audit",
        label: "Audit",
        icon: "report",
        route: "/admin/audit",
      },
      {
        id: "admin-api-usage",
        label: "API Usage",
        icon: "api",
        route: "/admin/api-monitoring",
        accessPath: "/admin/api-usage",
      },
    ],
  },
];

export const NAV_FOOTER: readonly ShellNavItem[] = [
  { id: "feedback", label: "Send Feedback", icon: "feedback", route: "/help" },
  { id: "profile", label: "Profile", icon: "user", route: "/profile" },
  {
    id: "settings",
    label: "Settings",
    icon: "settings",
    route: "/settings",
    matrixId: 17,
  },
  { id: "help", label: "Help", icon: "help", route: "/help" },
];

/* ── Legacy fallback — mirrors src/components/layout/SidebarMenu.tsx, which
      shows this nav unconditionally for any authenticated session. ── */

export const LEGACY_NAV_GROUPS: readonly ShellNavGroup[] = [
  {
    id: "legacy-main",
    items: [
      {
        id: "dashboard",
        label: "Dashboard",
        icon: "dashboard",
        route: "/dashboard",
      },
      { id: "lists", label: "List Management", icon: "list", route: "/lists" },
      {
        id: "enrichment",
        label: "Data Enrichment",
        icon: "enrich",
        route: "/enrichment",
      },
      {
        id: "groups",
        label: "Data Groups",
        icon: "groups",
        route: "/data-groups",
      },
      {
        id: "connections",
        label: "Connections",
        icon: "connections",
        route: "/connections",
      },
      { id: "export", label: "Data Export", icon: "export", route: "/export" },
    ],
  },
];

/**
 * `Send Feedback` targets /help, not /feedback: this SPA has never mounted a
 * /feedback route, so the legacy entry landed on the in-shell 404. The ported
 * NAV_FOOTER already resolves feedback → /help (the Help Center carries the
 * support/contact surface); the legacy footer now matches it rather than
 * advertising a page that does not exist.
 */
export const LEGACY_NAV_FOOTER: readonly ShellNavItem[] = [
  {
    id: "feedback",
    label: "Send Feedback",
    icon: "feedback",
    route: "/help",
  },
  { id: "help", label: "Help", icon: "help", route: "/help" },
];

/* ── Role filtering (visibility only) ── */

/** Matrix half of the check — what the ratified role matrix grants. */
function matrixAllows(role: string, item: ShellNavItem): boolean {
  if (item.matrixId !== undefined) {
    const screen = SCREEN_MATRIX.find((s) => s.id === item.matrixId);
    if (!screen || screen.access === null) return false;
    const normalized = normalizeRole(role);
    return normalized.ok ? screen.access[normalized.role] : false;
  }
  return canAccess(role, item.accessPath ?? item.route).allowed;
}

/**
 * An entry is advertised only when the matrix grants it AND the live router
 * would actually render it. The live check uses the item's REAL `route` (what
 * clicking navigates to), never `accessPath` — the guard runs on the path the
 * browser lands on.
 */
function itemVisible(role: string, item: ShellNavItem): boolean {
  return matrixAllows(role, item) && liveGuardAllows(role, item.route);
}

export interface VisibleNav {
  groups: readonly ShellNavGroup[];
  footer: readonly ShellNavItem[];
  /** true = non-normalizing session, legacy SidebarMenu nav in effect. */
  legacy: boolean;
}

function filterGroups(
  groups: readonly ShellNavGroup[],
  // eslint-disable-next-line no-unused-vars -- param name in a type annotation
  visible: (item: ShellNavItem) => boolean,
): readonly ShellNavGroup[] {
  return groups
    .map((group) => ({
      ...group,
      items: group.items
        .filter(visible)
        .map((item) =>
          item.children
            ? { ...item, children: item.children.filter(visible) }
            : item,
        ),
    }))
    .filter((group) => group.items.length > 0);
}

/**
 * Resolve the nav a session may SEE.
 *
 * Canonical roles → kit nav filtered by matrix AND live guard; non-normalizing
 * roles → the legacy SidebarMenu nav, filtered by the live guard alone (the
 * matrix cannot speak for an unratified role, but the router still refuses it
 * — a `customer` session was shown List Management / Data Export and bounced
 * to /dashboard on click). `editor` loses nothing: the live guards on all six
 * legacy entries admit it.
 *
 * The RAW role is what both predicates receive: canAccess normalizes it
 * itself, and the live guard MUST see the unnormalized claim because that is
 * what RoleGuard compares against.
 */
export function visibleNavForRole(role: string | null | undefined): VisibleNav {
  const rawRole = role ?? "";
  const normalized = normalizeRole(role);
  if (!normalized.ok) {
    return {
      groups: filterGroups(LEGACY_NAV_GROUPS, (item) =>
        liveGuardAllows(rawRole, item.route),
      ),
      footer: LEGACY_NAV_FOOTER.filter((item) =>
        liveGuardAllows(rawRole, item.route),
      ),
      legacy: true,
    };
  }
  const groups = filterGroups(NAV_GROUPS, (item) => itemVisible(rawRole, item));
  const footer = NAV_FOOTER.filter((item) => itemVisible(rawRole, item));
  return { groups, footer, legacy: false };
}
